import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { toast } from "sonner";

import { EarlyAccessNotice, ProductShell, UpgradeNotice } from "@/components/app/product-shell";
import { useEntitlements } from "@/lib/billing/use-entitlements";
import { ErrorBoundary } from "@/components/error-boundary";
import {
  CONSENT_STATUSES,
  listContacts,
  upsertContact,
  type Contact,
  type ConsentStatus,
} from "@/lib/pipeline/pipeline.functions";
import {
  CHANNELS,
  CHANNEL_LABEL,
  generateFollowUpPlan,
  guidanceFor,
  listFollowUpTasks,
  setTaskStatus,
  upsertFollowUpTask,
  type Channel,
  type FollowUpTask,
} from "@/lib/followup/followup.functions";

export const Route = createFileRoute("/_authenticated/app/follow-up")({
  head: () => ({
    meta: [
      { title: "Follow-up — AROS FLOW" },
      {
        name: "description",
        content:
          "Manual follow-up tasks with consent, permitted channels, opt-out and do-not-call recorded on every contact.",
      },
      { property: "og:title", content: "Follow-up — AROS FLOW" },
      {
        property: "og:description",
        content: "Track who to call back and when, with consent recorded on every contact.",
      },
      { property: "og:type", content: "website" },
    ],
  }),
  component: FollowUpPage,
});

const CONSENT_LABEL: Record<ConsentStatus, string> = {
  unknown: "Unknown",
  inbound: "They contacted us",
  express_written: "Express written consent",
  implied: "Implied / existing relationship",
  revoked: "Revoked",
};

function FollowUpPage() {
  return (
    <ProductShell
      title="Follow-Up"
      subtitle="Who to contact next, and what you are actually permitted to send them."
    >
      <ErrorBoundary label="Follow-up crashed">
        <FollowUpBody />
      </ErrorBoundary>
    </ProductShell>
  );
}

function FollowUpBody() {
  const ent = useEntitlements();
  const qc = useQueryClient();
  const fetchContacts = useServerFn(listContacts);
  const saveContact = useServerFn(upsertContact);

  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ["contacts"],
    queryFn: () => fetchContacts(),
  });

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [consent, setConsent] = useState<ConsentStatus>("unknown");
  const [followUp, setFollowUp] = useState("");

  const create = useMutation({
    mutationFn: () =>
      saveContact({
        data: {
          name,
          phone: phone || null,
          consentStatus: consent,
          consentSource: consent === "unknown" ? null : "Recorded manually in ArosFlow",
          nextFollowUp: followUp || null,
        },
      }),
    onSuccess: () => {
      toast.success("Contact saved");
      setName("");
      setPhone("");
      setFollowUp("");
      setConsent("unknown");
      qc.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const due = contacts
    .filter((c: Contact) => c.nextFollowUp)
    .sort((a: Contact, b: Contact) => (a.nextFollowUp ?? "").localeCompare(b.nextFollowUp ?? ""));

  if (!ent.loading && !ent.inPlan("pipeline")) {
    return <UpgradeNotice feature="Consent-aware follow-up planning" plan="Pro" />;
  }

  return (
    <div className="space-y-5">
      <EarlyAccessNotice
        title="Automated SMS and email sequences"
        body="Follow-up in this build is manual: ArosFlow tells you who is due and what consent you hold. It does not send anything on your behalf."
        requires="Messaging provider integration. Consent, permitted channels, opt-out and do-not-call are already recorded so outreach can be compliant the day it turns on."
      />

      <form
        className="grid gap-3 rounded-xl border border-[var(--glass-border)] bg-card p-4 sm:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim()) return;
          create.mutate();
        }}
      >
        <div>
          <label htmlFor="c-name" className="text-[11px] text-muted-foreground">
            Name
          </label>
          <input
            id="c-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="c-phone" className="text-[11px] text-muted-foreground">
            Phone
          </label>
          <input
            id="c-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="c-consent" className="text-[11px] text-muted-foreground">
            Consent
          </label>
          <select
            id="c-consent"
            value={consent}
            onChange={(e) => setConsent(e.target.value as ConsentStatus)}
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          >
            {CONSENT_STATUSES.map((c) => (
              <option key={c} value={c}>
                {CONSENT_LABEL[c]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="c-follow" className="text-[11px] text-muted-foreground">
            Next follow-up
          </label>
          <input
            id="c-follow"
            type="date"
            value={followUp}
            onChange={(e) => setFollowUp(e.target.value)}
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </div>
        <div className="sm:col-span-4">
          <button
            type="submit"
            disabled={create.isPending}
            className="focus-key rounded-lg gradient-gold px-4 py-2 text-sm font-semibold text-black disabled:opacity-60"
          >
            Save contact
          </button>
        </div>
      </form>

      <section>
        <h2 className="text-sm font-semibold">Due and scheduled</h2>
        {isLoading ? (
          <p className="mt-2 text-sm text-muted-foreground">Loading…</p>
        ) : due.length === 0 ? (
          <p className="mt-2 rounded-xl border border-[var(--glass-border)] bg-card p-5 text-sm text-muted-foreground">
            Nothing scheduled. Add a contact with a follow-up date above.
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {due.map((c: Contact) => {
              const g = guidanceFor(c);
              return (
                <li
                  key={c.id}
                  className="rounded-xl border border-[var(--glass-border)] bg-card p-4 text-xs"
                >
                  <div className="flex flex-wrap items-center gap-3">
                    <span className="font-semibold">{c.name}</span>
                    {c.phone && <span className="text-muted-foreground">{c.phone}</span>}
                    <span className="rounded-full border border-[var(--glass-border)] px-2 py-0.5 text-[10px]">
                      {CONSENT_LABEL[c.consentStatus]}
                    </span>
                    {c.doNotCall && <span className="text-destructive">Do not call</span>}
                    {c.optOut && <span className="text-destructive">Opted out</span>}
                    <span className="ml-auto text-muted-foreground">
                      Due {new Date(c.nextFollowUp as string).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    Suggested channel:{" "}
                    <span className="text-gold">{CHANNEL_LABEL[g.recommended]}</span>
                    {g.allowed.length > 0 && (
                      <> · permitted: {g.allowed.map((ch) => CHANNEL_LABEL[ch]).join(", ")}</>
                    )}
                  </div>
                  <p className="mt-1 text-[10px] text-muted-foreground">{g.explanation}</p>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <PlanGenerator contacts={contacts} />

      <TaskSection />
    </div>
  );
}

function PlanGenerator({ contacts }: { contacts: Contact[] }) {
  const qc = useQueryClient();
  const generate = useServerFn(generateFollowUpPlan);
  const [contactId, setContactId] = useState("");
  const [result, setResult] = useState<Awaited<ReturnType<typeof generateFollowUpPlan>> | null>(
    null,
  );

  const run = useMutation({
    mutationFn: () => generate({ data: { contactId } }),
    onSuccess: (r) => {
      setResult(r);
      if (r.created > 0) toast.success(`${r.created} follow-up tasks created`);
      else toast.message("No tasks created", { description: r.summary });
      qc.invalidateQueries({ queryKey: ["follow-up-tasks"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <section>
      <h2 className="text-sm font-semibold">Build a follow-up plan</h2>
      <p className="mt-1 text-[11px] text-muted-foreground">
        Day 1, 3, 7, 14, 30, 60 and 90. Each step is checked against the consent you recorded and
        created as a task for you to do. Nothing is sent automatically.
      </p>
      <div className="mt-2 flex flex-wrap items-end gap-3 rounded-xl border border-[var(--glass-border)] bg-card p-4">
        <div className="min-w-[220px] flex-1">
          <label htmlFor="plan-contact" className="text-[11px] text-muted-foreground">
            Contact
          </label>
          <select
            id="plan-contact"
            value={contactId}
            onChange={(e) => setContactId(e.target.value)}
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          >
            <option value="">Choose a contact…</option>
            {contacts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          disabled={!contactId || run.isPending}
          onClick={() => run.mutate()}
          className="focus-key rounded-lg gradient-gold px-4 py-2 text-sm font-semibold text-black disabled:opacity-60"
        >
          {run.isPending ? "Building…" : "Generate plan"}
        </button>
      </div>

      {result && (
        <div className="mt-2 rounded-xl border border-[var(--glass-border)] bg-card p-4 text-xs">
          <p className="text-[11px] text-muted-foreground">{result.summary}</p>
          <ul className="mt-3 space-y-2">
            {result.steps.map((s) => (
              <li key={s.title} className="flex flex-wrap items-center gap-2">
                <span
                  className={s.blocked ? "text-muted-foreground line-through" : "font-semibold"}
                >
                  {s.title}
                </span>
                <span className="rounded-full border border-[var(--glass-border)] px-2 py-0.5 text-[10px]">
                  {CHANNEL_LABEL[s.channel]}
                </span>
                <span className="text-muted-foreground">
                  {new Date(s.dueAt).toLocaleDateString()}
                </span>
                {s.note && <span className="basis-full text-[10px] text-gold">{s.note}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function TaskSection() {
  const qc = useQueryClient();
  const fetchTasks = useServerFn(listFollowUpTasks);
  const saveTask = useServerFn(upsertFollowUpTask);
  const markTask = useServerFn(setTaskStatus);

  const { data: tasks = [] } = useQuery({
    queryKey: ["follow-up-tasks"],
    queryFn: () => fetchTasks(),
  });

  const [title, setTitle] = useState("");
  const [channel, setChannel] = useState<Channel>("call");
  const [dueAt, setDueAt] = useState("");

  const invalidate = () => qc.invalidateQueries({ queryKey: ["follow-up-tasks"] });

  const create = useMutation({
    mutationFn: () => saveTask({ data: { title, channel, dueAt: dueAt || null } }),
    onSuccess: () => {
      setTitle("");
      setDueAt("");
      toast.success("Task added");
      invalidate();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const complete = useMutation({
    mutationFn: (id: string) => markTask({ data: { id, status: "done" } }),
    onSuccess: invalidate,
  });

  const open = tasks.filter((t: FollowUpTask) => t.status === "open");

  return (
    <section>
      <h2 className="text-sm font-semibold">Your follow-up tasks</h2>
      <form
        className="mt-2 grid gap-3 rounded-xl border border-[var(--glass-border)] bg-card p-4 sm:grid-cols-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!title.trim()) return;
          create.mutate();
        }}
      >
        <div className="sm:col-span-2">
          <label htmlFor="t-title" className="text-[11px] text-muted-foreground">
            What needs doing
          </label>
          <input
            id="t-title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Call back about the roof quote"
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label htmlFor="t-channel" className="text-[11px] text-muted-foreground">
            Channel
          </label>
          <select
            id="t-channel"
            value={channel}
            onChange={(e) => setChannel(e.target.value as Channel)}
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          >
            {CHANNELS.filter((c) => c !== "none").map((c) => (
              <option key={c} value={c}>
                {CHANNEL_LABEL[c]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="t-due" className="text-[11px] text-muted-foreground">
            Due
          </label>
          <input
            id="t-due"
            type="date"
            value={dueAt}
            onChange={(e) => setDueAt(e.target.value)}
            className="focus-key mt-1 w-full rounded-lg border border-[var(--glass-border)] bg-input px-3 py-2 text-sm"
          />
        </div>
        <div className="sm:col-span-4">
          <button
            type="submit"
            disabled={create.isPending}
            className="focus-key rounded-lg gradient-gold px-4 py-2 text-sm font-semibold text-black disabled:opacity-60"
          >
            Add task
          </button>
        </div>
      </form>

      {open.length === 0 ? (
        <p className="mt-2 rounded-xl border border-[var(--glass-border)] bg-card p-5 text-sm text-muted-foreground">
          No open tasks. ArosFlow never sends anything for you — it just keeps the list honest.
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {open.map((t: FollowUpTask) => (
            <li
              key={t.id}
              className="flex flex-wrap items-center gap-3 rounded-xl border border-[var(--glass-border)] bg-card p-4 text-xs"
            >
              <span className="font-semibold">{t.title}</span>
              <span className="rounded-full border border-[var(--glass-border)] px-2 py-0.5 text-[10px]">
                {CHANNEL_LABEL[t.channel]}
              </span>
              {t.dueAt && (
                <span className="text-muted-foreground">
                  Due {new Date(t.dueAt).toLocaleDateString()}
                </span>
              )}
              <button
                type="button"
                onClick={() => complete.mutate(t.id)}
                className="focus-key ml-auto rounded-md border border-white/15 px-2 py-1 text-[11px] text-white/70 hover:bg-white/5"
              >
                Done
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
